import React, { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef } from 'react';
import { Box, Meter, Stack, Text, Clock } from 'grommet';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { getActualSessionTimePassedSeconds, getIsPaused } from 'src/reducers/workSessions/workSessionsSelectors';
import {
  setSessionTimePassed,
  startManualRewind,
  stopManualRewind,
  toggleIsPaused,
} from 'src/reducers/workSessions/workSessionsReducer';
import { getClockTime } from 'src/utils/date.utils';

const StackStyled = styled(Stack)`
  cursor: pointer;
  user-select: none;
`;

interface Props {
  title: string;
  itemOffset: number;
  itemDuration: number;
  sessionDuration: number;
}

export default function SessionProgressBar({ title, itemDuration, itemOffset, sessionDuration }: Props): JSX.Element {
  const dispatch = useDispatch();
  const timePassed = useSelector(getActualSessionTimePassedSeconds);
  const isPaused = useSelector(getIsPaused);
  const stackRef = useRef<HTMLDivElement>(null);
  const isRewindRef = useRef(false);
  const isWasPausedRef = useRef(isPaused);
  const itemTimeLeft = Math.max(0, Math.min(itemDuration, itemDuration - timePassed + itemOffset));
  const progress = 100 * (1 - itemTimeLeft / itemDuration);
  const isEndRef = useRef(!itemTimeLeft);

  const rewind = useCallback(
    (clientX: number) => {
      if (!stackRef.current) {
        return;
      }

      const { left: containerLeft, width } = stackRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(1, (clientX - containerLeft) / width));

      dispatch(setSessionTimePassed(itemOffset + itemDuration * percentage));
    },
    [dispatch, itemDuration, itemOffset]
  );

  const handleOnMouseDown = (event: ReactMouseEvent) => {
    isRewindRef.current = true;
    isWasPausedRef.current = isPaused;
    rewind(event.clientX);
  };

  const stopRewind = useCallback(() => {
    if (!isRewindRef.current) {
      return;
    }

    isRewindRef.current = false;
    dispatch(stopManualRewind());

    if (!isWasPausedRef.current && !isEndRef.current) {
      dispatch(toggleIsPaused());
    }
  }, [dispatch]);

  const handleOnMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isRewindRef.current) {
        dispatch(startManualRewind());
        rewind(event.clientX);
      }
    },
    [dispatch, rewind]
  );

  useEffect(() => {
    isEndRef.current = !itemTimeLeft;
  }, [itemTimeLeft]);

  useEffect(() => {
    window.addEventListener('mousemove', handleOnMouseMove);
    window.addEventListener('mouseup', stopRewind);
    window.addEventListener('mouseleave', stopRewind);

    return () => {
      window.removeEventListener('mousemove', handleOnMouseMove);
      window.removeEventListener('mouseup', stopRewind);
      window.removeEventListener('mouseleave', stopRewind);
    };
  }, [handleOnMouseMove, stopRewind]);

  const renderClock = (duration: number) => {
    const date = new Date(Math.round(duration) * 1000);

    return <Clock type='digital' time={getClockTime(date, true)} precision='minutes' run={false} />;
  };

  return (
    <Box width={`${100 * (itemDuration / sessionDuration)}%`} direction='column' align='center'>
      <Text>{title}</Text>
      <StackStyled ref={stackRef} title='Click to rewind' onMouseDown={handleOnMouseDown} anchor='center' fill>
        <Meter type='bar' value={progress} size='full' />
        {renderClock(itemTimeLeft)}
      </StackStyled>
    </Box>
  );
}
